import { Module } from "vuex";
import { AutonomousFormLegalPersonBaseFieldService } from "../../services/AutonomousFormLegalPersonBaseFieldService";
import { RootState } from "../RootState";
import { AutonomousFormBaseField } from "../../models/responses/AutonomousFormBaseField";
import { FlowBaseFieldUpdateRequest } from "../../models/requests/FlowBaseFieldUpdateRequest";
import { FieldLegalPersonTypes } from "../../models/FieldLegalPersonTypes";

export type FlowFieldKeyValue = {
  name: string;
  field: AutonomousFormBaseField;
};

export interface AutonomousFormLegalPersonBaseFieldState {
  autonomousFormLegalPersonBaseFields: FlowFieldKeyValue[];
}

async function getFieldWithName(
  name: string,
  flowId: number
): Promise<FlowFieldKeyValue> {
  const response = await AutonomousFormLegalPersonBaseFieldService.getField(
    name,
    flowId
  );
  return { name: name, field: response.data };
}

// Magasin VueX qui coordonne l'échange d'infos entre les différentes vues.
export const autonomousFormLegalPersonBaseFieldModule: Module<
  AutonomousFormLegalPersonBaseFieldState,
  RootState
> = {
  namespaced: true,
  // Données partagées du magasin (ne peuvent pas être modifiées directement)
  state: {
    autonomousFormLegalPersonBaseFields: []
  },

  // Accesseurs pour propriétés calculées
  getters: {},

  // Opérations de modification des données
  mutations: {
    /**
     * Updates autonomousFormBaseFields
     */
    setAutonomousFormLegalPersonBaseFields(state, value: FlowFieldKeyValue[]) {
      state.autonomousFormLegalPersonBaseFields = value;
    },

    /**
     * Updates one base field by name
     * @param state
     * @param value
     */
    setAutonomousFormLegalPersonBaseField(
      state,
      value: { name: string; field: AutonomousFormBaseField }
    ) {
      const index = state.autonomousFormLegalPersonBaseFields.findIndex(
        val => val.name === value.name
      );
      const keyValue: FlowFieldKeyValue = {
        name: value.name,
        field: value.field
      };
      if (index > -1) {
        state.autonomousFormLegalPersonBaseFields.splice(index, 1, keyValue);
      }
    }
  },

  // Actions pouvant être appelées par les vues pour agir sur le magasin
  actions: {
    async loadField({ commit }, value: { name: string; flowId: number }) {
      const response = await AutonomousFormLegalPersonBaseFieldService.getField(
        value.name,
        value.flowId
      );
      commit("setAutonomousFormLegalPersonBaseField", {
        name: value.name,
        field: response.data
      });
      return response.data;
    },

    async clearField(
      { commit },
      value: { name: string; flowId: number; field: FlowBaseFieldUpdateRequest }
    ) {
      await AutonomousFormLegalPersonBaseFieldService.clearField(
        value.name,
        value.flowId
      );
      const getResponse = await AutonomousFormLegalPersonBaseFieldService.getField(
        value.name,
        value.flowId
      );
      commit("setAutonomousFormLegalPersonBaseField", {
        name: value.name,
        field: getResponse.data
      });
      return getResponse.data;
    },

    async setField(
      context,
      value: { name: string; flowId: number; field: FlowBaseFieldUpdateRequest }
    ) {
      const response = await AutonomousFormLegalPersonBaseFieldService.setField(
        value.name,
        value.flowId,
        value.field
      );
      context.commit("setAutonomousFormLegalPersonBaseField", {
        name: value.name,
        field: response.data
      });
      return response.data;
    },

    async loadAllFields(context, flowId: number) {
      const results = await Promise.all(
        FieldLegalPersonTypes.map(val => getFieldWithName(val.type, flowId))
      );

      context.commit("setAutonomousFormLegalPersonBaseFields", results);
      return results;
    }
  }
};
